import { Loader2, X } from "lucide-react";
import { useState } from "react";

export interface AddNoteModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (title: string, content: string) => void;
  isSaving: boolean;
}

export function AddNoteModal({ open, onClose, onSave, isSaving }: Readonly<AddNoteModalProps>) {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  if (!open) return null;

  const handleClose = () => {
    setTitle("");
    setContent("");
    onClose();
  };

  const handleSave = () => {
    if (!title.trim() || !content.trim()) return;
    onSave(title, content);
  };

  return (
    <div className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">Add Note</h2>
          <button
            onClick={handleClose}
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-4">
          <div>
            <label htmlFor="note-title" className="block text-sm font-medium text-content-secondary mb-1.5">
              Title
            </label>
            <input
              id="note-title"
              type="text"
              placeholder="e.g., Meeting Notes, Product Requirements"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
            />
          </div>
          <div>
            <label htmlFor="note-content" className="block text-sm font-medium text-content-secondary mb-1.5">
              Content
            </label>
            <textarea
              id="note-content"
              rows={10}
              placeholder="Paste or type your text here. Supports markdown formatting."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none font-mono text-sm"
            />
            <p className="text-xs text-content-quaternary mt-1.5">
              The content will be saved as a .md file and indexed for RAG search.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !title.trim() || !content.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isSaving ? "Saving..." : "Save & Index"}
          </button>
        </div>
      </div>
    </div>
  );
}
